'use client';

import { useRef } from 'react';
import Link from 'next/link';

import { VerticalTabs, type TabContent } from './vertical-tabs';

const STICKY_TOP = 100;

const nielsenUrl =
  'https://www.nielsen.com/insights/2024/maximizing-roi-in-a-fragmented-world-nielsen-annual-marketing-report/';

const tabs: TabContent[] = [
  {
    label: 'Become visible',
    heading: "Build your brand's visibility and trust",
    content: (
      <ul
        className="ml-4.5 flex list-outside list-disc flex-col gap-y-2"
        data-test="get-seen-become-visible-list"
      >
        <li data-test="get-seen-become-visible-list-item-1">
          Target the audiences who matter most to your business.
        </li>
        <li data-test="get-seen-become-visible-list-item-2">
          Boost your visibility on the highest-impact networks, channels, and
          streaming apps.
        </li>
        <li data-test="get-seen-become-visible-list-item-3">
          See how your brand awareness grows with every TV campaign.
        </li>
      </ul>
    ),
    imageUrl: '/home-tab-1.png',
    laptopImageUrl: '/home-tab-1-laptop.png',
  },
  {
    label: 'Attract customers',
    heading: 'Bring more of the right people to your business',
    content: (
      <ul
        className="ml-4.5 flex list-outside list-disc flex-col gap-y-2"
        data-test="get-seen-attract-customers-list"
      >
        <li data-test="get-seen-attract-customers-list-item-1">
          Control who sees your ads with geo, demo, and behavioral targeting.
        </li>
        <li data-test="get-seen-attract-customers-list-item-2">
          Reporting that shows top-performing networks, time slots, shows, and
          more.
        </li>
        <li data-test="get-seen-attract-customers-list-item-3">
          Measure conversions easily with the Skybeam Pixel.
        </li>
      </ul>
    ),
    imageUrl: '/home-tab-2.png',
    laptopImageUrl: '/home-tab-2-laptop.png',
  },
  {
    label: 'Increase sales',
    heading: 'Turn viewers into customers',
    content: (
      <ul
        className="ml-4.5 flex list-outside list-disc flex-col gap-y-2"
        data-test="get-seen-increase-sales-list"
      >
        <li data-test="get-seen-increase-sales-list-item-1">
          Deliver personalized TV ads that drive real purchases and store
          visits.
        </li>
        <li data-test="get-seen-increase-sales-list-item-2">
          Reach high-intent shoppers across premium networks, channels and apps.
        </li>
        <li data-test="get-seen-increase-sales-list-item-3">
          Measure conversions and optimize for sales growth.
        </li>
      </ul>
    ),
    imageUrl: '/home-tab-3.png',
    laptopImageUrl: '/home-tab-3-laptop.png',
  },
  {
    label: 'Halo Effect',
    heading: 'Lift every channel with the power of TV',
    content: (
      <div className="flex flex-col gap-y-3">
        <div data-test="get-seen-halo-effect-intro">
          TV advertising delivers a 2.1× ROI multiplier when paired with digital
          marketing (
          <Link
            className="underline"
            data-test="get-seen-halo-effect-nielsen-link"
            href={nielsenUrl}
            target="_blank"
          >
            Nielsen
          </Link>
          ).
        </div>
        <ul
          className="ml-4.5 flex list-outside list-disc flex-col gap-y-2"
          data-test="get-seen-halo-effect-list"
        >
          <li data-test="get-seen-halo-effect-list-item-1">
            Drive incremental lift across your digital channels.
          </li>
          <li data-test="get-seen-halo-effect-list-item-2">
            Reach audiences that make your other channels perform better.
          </li>
          <li data-test="get-seen-halo-effect-list-item-3">
            Spark more engagement across your entire funnel.
          </li>
        </ul>
      </div>
    ),
    imageUrl: '/home-tab-4.png',
    laptopImageUrl: '/home-tab-4-laptop.png',
  },
];

export const GetSeenSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={sectionRef}
      data-test="get-seen-section"
      data-section="get-seen-section"
      className="desktop:pb-13 tablet:py-20 desktop:bg-white flex justify-center bg-white px-6 py-14"
    >
      <div
        ref={trackRef}
        className="desktop:block hidden w-full"
        data-test="get-seen-desktop-wrapper"
      >
        <div
          className="sticky flex w-full justify-center"
          style={{
            top: `${STICKY_TOP}px`,
            height: `calc(100vh - ${STICKY_TOP}px)`,
          }}
        >
          <div className="desktop:max-w-284 tablet:max-w-[520px] desktop:justify-center laptop:max-w-[880px] desktop:gap-y-12.5 tablet:gap-y-10 laptop:gap-y-20 flex w-full flex-col items-center gap-y-7 px-6">
            <h2 className="heading-marketing-2 tablet:max-w-full max-w-50 text-center font-light">
              Get Seen. Grow Faster.
            </h2>
            <VerticalTabs
              data={tabs}
              sectionRef={sectionRef}
              trackRef={trackRef}
            />
          </div>
        </div>
      </div>

      <div className="desktop:hidden flex w-full justify-center">
        <div className="desktop:max-w-284 tablet:max-w-[520px] laptop:max-w-[880px] desktop:gap-y-12.5 tablet:gap-y-10 laptop:gap-y-20 flex w-full flex-col items-center gap-y-7">
          <h2 className="heading-marketing-2 tablet:max-w-full max-w-50 text-center font-light">
            Get Seen. Grow Faster.
          </h2>
          <VerticalTabs
            data={tabs}
            sectionRef={sectionRef}
            trackRef={trackRef}
          />
        </div>
      </div>
    </div>
  );
};
